<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    @click="handleBackdropClick"
  >
    <div
      class="bg-white rounded-lg w-[600px] max-h-[80vh] flex flex-col"
      @click.stop
    >
      <!-- 헤더 -->
      <div
        class="flex justify-between items-center px-6 py-4 border-b border-gray-200"
      >
        <h3 class="text-lg font-medium text-gray-900">발신자 정보</h3>
        <button @click="onClose" class="text-gray-400 hover:text-gray-500">
          <svg
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- 컨텐츠 -->
      <div class="flex-1 overflow-y-auto px-6 py-4 mb-4">
        <div
          v-if="groupedSenderInfos.length === 0"
          class="text-gray-500 text-center py-4"
        >
          데이터가 없습니다
        </div>

        <div
          v-for="(group, index) in groupedSenderInfos"
          :key="index"
          class="mb-2 last:mb-0 hover:bg-gray-50 rounded-lg p-3 cursor-pointer group"
          @click="copySenderName(group.senderName, index)"
        >
          <!-- 발신자 정보 헤더 -->
          <div class="mb-2">
            <span class="flex items-center gap-2">
              <span class="text-lg font-medium text-gray-900">{{
                group.senderName
              }}</span>
              <span class="text-sm text-gray-500">{{
                group.senderAddress
              }}</span>
              <span
                v-if="copyStatus[index]"
                class="text-xs text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                복사완료
              </span>
            </span>
          </div>

          <!-- 채팅 내용 목록 -->
          <div class="space-y-2 pl-2">
            <div
              v-for="(chat, chatIndex) in group.chats"
              :key="chatIndex"
              class="flex items-start gap-3 text-sm border-l-2 border-gray-100 pl-3"
            >
              <div class="text-xs text-gray-400 whitespace-nowrap pt-0.5">
                {{ formatTime(chat.chatDateTime) }}
              </div>
              <div class="text-gray-600 break-all">{{ chat.content }}</div>
            </div>
          </div>

          <!-- 구분선 (마지막 항목 제외) -->
          <div
            v-if="index !== groupedSenderInfos.length - 1"
            class="border-b border-gray-100 my-3"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { format } from 'date-fns';

export default {
  name: 'SenderInfoModal',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    senderInfos: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const copyStatus = ref({});

    const groupedSenderInfos = computed(() => {
      const groups = {};

      props.senderInfos.forEach(info => {
        if (!groups[info.senderName]) {
          groups[info.senderName] = {
            senderName: info.senderName,
            senderAddress: info.senderAddress,
            chats: [],
          };
        }
        groups[info.senderName].chats.push({
          content: info.content,
          chatDateTime: info.chatDateTime,
        });
      });

      return Object.values(groups).sort((a, b) => {
        const latestA = Math.max(...a.chats.map(c => new Date(c.chatDateTime)));
        const latestB = Math.max(...b.chats.map(c => new Date(c.chatDateTime)));
        return latestB - latestA;
      });
    });

    const formatTime = datetime => {
      return format(new Date(datetime), 'HH:mm:ss');
    };

    const copySenderName = async (senderName, index) => {
      try {
        await navigator.clipboard.writeText(senderName);
        copyStatus.value[index] = true;

        // 2초 후 복사 상태 초기화
        setTimeout(() => {
          copyStatus.value[index] = false;
        }, 2000);
      } catch (error) {
        console.error('복사 실패:', error);
      }
    };

    const onClose = () => {
      emit('close');
      copyStatus.value = {}; // 모달 닫을 때 복사 상태 초기화
    };

    const handleBackdropClick = event => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    };

    return {
      groupedSenderInfos,
      formatTime,
      copySenderName,
      copyStatus,
      onClose,
      handleBackdropClick,
    };
  },
};
</script>

<style scoped>
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 #f7fafc;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f7fafc;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: #e2e8f0;
  border-radius: 2px;
}

/* 툴팁 스타일 */
[data-tooltip] {
  position: relative;
}

[data-tooltip]:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  left: 0;
  top: -25px;
  background: #333;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 10;
}

/* hover 효과를 위한 추가 스타일 */
.group {
  transition: background-color 0.2s ease;
}

.group:hover {
  background-color: rgba(243, 244, 246, 0.8);
}
</style>
